
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/utils/notification';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarInset,
  SidebarFooter,
  SidebarTrigger,
  useSidebar
} from '@/components/ui/sidebar';

import { Loader2, LogOut } from 'lucide-react';
import { Home, User, Calendar, Bookmark, FileText, Settings, DollarSign, Briefcase, Clock, History, CalendarDays, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

import ProfileDetails from '@/components/dashboard/practitioner/ProfileDetails';
import ServicesManager from '@/components/dashboard/practitioner/ServicesManager';
import AvailabilitySettings from '@/components/dashboard/practitioner/AvailabilitySettings';
import CertificationDashboard from '@/components/dashboard/practitioner/CertificationDashboard';
import FinancesManager from '@/components/dashboard/practitioner/FinancesManager';
import { ReportsManager } from '@/components/dashboard/practitioner/ReportsManager';
import AppointmentsList from '@/components/dashboard/practitioner/AppointmentsList';
import PractitionerSettings from '@/components/dashboard/practitioner/PractitionerSettings';
import Navbar from '@/components/Navbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarMenuButtonWithAutoHide } from '@/components/dashboard/shared/SidebarMenuButtonWithAutoHide';
import { PractitionerUpcomingAppointments } from '@/components/dashboard/practitioner/appointments/PractitionerUpcomingAppointments';
import { PractitionerAppointmentHistory } from '@/components/dashboard/practitioner/appointments/PractitionerAppointmentHistory';
import CalendarView from '@/components/calendar/CalendarView';

import { UnifiedLoader } from '@/components/dashboard/shared/UnifiedLoader';
import { UnifiedError } from '@/components/dashboard/shared/UnifiedError';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUnreadSystemMessages } from '@/hooks/useUnreadSystemMessages';
import SystemMessagesList from '@/components/dashboard/shared/SystemMessagesList';
import { getFullName } from '@/utils/name-helpers';
import { RefundNotificationSystem } from '@/components/dashboard/practitioner/RefundNotificationSystem';
import { TimezoneDetectionAlert } from '@/components/dashboard/shared/TimezoneDetectionAlert';
import { UnreadMessagesAlert } from '@/components/dashboard/practitioner/UnreadMessagesAlert';
import { usePractitionerAppointments } from '@/components/dashboard/practitioner/hooks/usePractitionerAppointments';
import { usePractitionerAppointmentHistory } from '@/components/dashboard/practitioner/hooks/usePractitionerAppointmentHistory';
import { LateCancellationRequestsList } from '@/components/dashboard/practitioner/LateCancellationRequestsList';


const PractitionerDashboard: React.FC = () => {
  const { user, isLoading: authLoading, activeRole, signOut, roleReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practitionerProfile, setPractitionerProfile] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);

  const isMobile = useIsMobile();

  // Unread messages hook
  const { unreadCounts, refreshUnreadCounts } = useUnreadMessages({
    userId: undefined, // Will be set by the hook from auth context
    userType: 'practitioner'
  });

  // Unread system messages hook
  const { unreadCount: systemMessagesCount, markAsViewed: markSystemMessagesAsViewed } = useUnreadSystemMessages('practitioner');

  // Local state for system messages unread count (from SystemMessagesList component)
  const [systemMessagesUnreadCount, setSystemMessagesUnreadCount] = useState(0);

  // Fetch upcoming and history appointments at dashboard level
  const { appointments: upcomingAppointments } = usePractitionerAppointments();
  const { appointments: historyAppointments } = usePractitionerAppointmentHistory();

  // Calculate unread counts for upcoming and history at dashboard level
  const upcomingUnreadCount = useMemo(() => {
    return upcomingAppointments.reduce((sum, appointment) => {
      return sum + (unreadCounts.unreadByBooking[appointment.id] || 0);
    }, 0);
  }, [upcomingAppointments, unreadCounts.unreadByBooking]);

  const historyUnreadCount = useMemo(() => {
    return historyAppointments.reduce((sum, appointment) => {
      return sum + (unreadCounts.unreadByBooking[appointment.id] || 0);
    }, 0);
  }, [historyAppointments, unreadCounts.unreadByBooking]);

  // Memoize initial tab from URL
  const initialTab = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('tab') || 'home';
  }, [location.search]);

  // Get appointmentId from URL for auto-scrolling
  const appointmentId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('appointmentId');
  }, [location.search]);

  const [activeTab, setActiveTab] = useState(initialTab);

  // Mark system messages as viewed when initially on home tab
  useEffect(() => {
    if (activeTab === 'home') {
      markSystemMessagesAsViewed();
    }
  }, []); // Only run once on mount

  // Optimized tab change handler with useCallback
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    navigate(`/practitioner-dashboard?tab=${tab}`, { replace: true });

    // Mark system messages as viewed when navigating to home tab
    if (tab === 'home') {
      markSystemMessagesAsViewed();
    }
  }, [navigate, markSystemMessagesAsViewed]);

  // Optimized sign out handler with useCallback
  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      navigate('/');
      notify.success("Logged Out", "You've been successfully logged out");
    } catch (error) {
      console.error('Sign out error:', error);
      notify.error("Error", "Failed to sign out. Please try again.");
    }
  }, [signOut, navigate]);

  // Handle navigation to refund request
  const handleNavigateToRefund = useCallback((bookingId: string) => {
    console.log('🔗 Navigating to refund for booking:', bookingId);
    // Navigate to upcoming appointments tab with the specific booking
    setActiveTab('upcoming');
    navigate(`/practitioner-dashboard?tab=upcoming&appointmentId=${bookingId}`, { replace: true });
  }, [navigate]);

  // Handle navigation to appointment from unread messages
  const handleNavigateToAppointment = useCallback((bookingId: string) => {
    console.log('🔗 Navigating to appointment from unread message:', bookingId);
    // Navigate to upcoming appointments tab with the specific booking
    setActiveTab('upcoming');
    navigate(`/practitioner-dashboard?tab=upcoming&appointmentId=${bookingId}`, { replace: true });
  }, [navigate]);

  // Exposed fetcher with retry logic
  const fetchPractitionerProfile = React.useCallback(async (retryCount = 0) => {
    if (authLoading || !roleReady) return;

    setError(null);

    try {
      if (!user) {
        notify.error("Unauthorized", "Please login to view your dashboard");
        setIsLoading(false);
        navigate("/practitioner-auth");
        return;
      }

      console.log("Fetching practitioner profile for user ID:", user.id, "retry:", retryCount);
      const { data, error } = await supabase
        .from('practitioners')
        .select('*, profile_status, payout_details_status, stripe_account_id')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching practitioner profile:", error);
        // Retry up to 3 times with exponential backoff
        if (retryCount < 3) {
          const delay = (retryCount + 1) * 1000;
          console.log(`Retrying profile fetch in ${delay}ms...`);
          setTimeout(() => fetchPractitionerProfile(retryCount + 1), delay);
          return;
        }
        setError("Could not load your practitioner profile");
        setIsLoading(false);
        return;
      }

      if (!data) {
        console.log("No practitioner profile found. Waiting for post-confirm creation.");
        setProfileMissing(true);
        setIsLoading(false);
        return;
      }

      console.log("Fetched practitioner profile:", data);
      setPractitionerProfile(data);

      if (data.profile_status === 'pending_review') {
        notify.info("Profile Pending Review", "Your profile is currently under review by our team.");
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      if (retryCount < 3) {
        const delay = (retryCount + 1) * 1000;
        console.log(`Retrying after unexpected error in ${delay}ms...`);
        setTimeout(() => fetchPractitionerProfile(retryCount + 1), delay);
        return;
      }
      setError("An unexpected error occurred while loading your profile");
      setIsLoading(false);
    }
  }, [authLoading, roleReady, user, navigate]);

  // Initial check and fetch
  useEffect(() => {
    fetchPractitionerProfile();
  }, [fetchPractitionerProfile]);

  // Subscribe to practitioner profile creation for this user
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('practitioner-profile-watch')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'practitioners', filter: `id=eq.${user.id}` },
        () => {
          console.log('Detected practitioner profile INSERT for current user. Refetching...');
          setProfileMissing(false);
          setIsLoading(true);
          fetchPractitionerProfile();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchPractitionerProfile]);

  // Memoized tab content renderer
  const renderTabContent = useMemo(() => {
    if (!user?.id) return null;

    switch (activeTab) {
      case 'home':
        return (
          <div className="min-h-[400px] space-y-6">
            <div>
              <h2 className="text-2xl font-medium mb-4">{t('welcomeToDashboard')}</h2>
              <p className="text-gray-600 mb-6">
                {t('hello')} {getFullName(practitionerProfile) || user?.email?.split('@')[0] || 'there'}! {t('personalDashboard')}
              </p>
              <p className="text-gray-600 mb-6">
                {t('navigateToManageServices')}
              </p>
            </div>

            <SystemMessagesList 
              userType="practitioner" 
              onUnreadCountChange={setSystemMessagesUnreadCount}
            />
          </div>
        );
      case 'profile':
        return <ProfileDetails userId={user.id} />;
      case 'services':
        return <ServicesManager userId={user.id} />;
      case 'upcoming':
        return (
          <PractitionerUpcomingAppointments
            unreadByBooking={unreadCounts.unreadByBooking}
            appointmentId={appointmentId}
            onRefreshUnreadCounts={refreshUnreadCounts}
          />
        );
      case 'history':
        return (
          <PractitionerAppointmentHistory
            unreadByBooking={unreadCounts.unreadByBooking}
            appointmentId={appointmentId}
          />
        );
      case 'late-cancellations':
        return <LateCancellationRequestsList />;
      case 'calendar':
        return <CalendarView />;
      case 'availability':
        return <AvailabilitySettings userId={user.id} />;
      case 'certifications':
        return <CertificationDashboard userId={user.id} />;
      case 'finances':
        return <FinancesManager userId={user.id} />;
      case 'reports':
        return <ReportsManager userId={user.id} />;
      case 'settings':
        return <PractitionerSettings practitionerId={user.id} profile={practitionerProfile} setProfile={setPractitionerProfile} />;
      default:
        return null;
    }
  }, [activeTab, user?.id, practitionerProfile, t, appointmentId, unreadCounts.unreadByBooking, handleNavigateToAppointment]);

  if (authLoading || !roleReady || isLoading) {
    return <UnifiedLoader message={t('loading')} />;
  }

  if (error) {
    return (
      <UnifiedError 
        message={error} 
        onRetry={() => {
          setError(null);
          setIsLoading(true);
          // Force a re-check
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      {/* Timezone detection - runs once per session */}
      {user && practitionerProfile && (
        <TimezoneDetectionAlert
          userId={user.id}
          userType="practitioner"
          currentTimezone={practitionerProfile.timezone || 'America/New_York'}
        />
      )}
      <SidebarProvider>
        <div className="min-h-screen flex w-full pt-16">
          <Sidebar variant="sidebar" collapsible="icon" className="border-r border-gray-100 transition-all duration-300 ease-in-out">
            <SidebarHeader className="border-b px-4 py-3">
              <div className="text-lg font-bold text-amber-600">{t('dashboard')}</div>
            </SidebarHeader>
            <SidebarContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide
                    tooltip={t('home')}
                    onClick={() => handleTabChange('home')}
                    isActive={activeTab === 'home'}
                    showBadge={true}
                    unreadCount={systemMessagesUnreadCount}
                    countRefreshTrigger={systemMessagesUnreadCount}
                  >
                    <Home className="mr-2" />
                    <span>{t('home')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('profile')} 
                    onClick={() => handleTabChange('profile')}
                    isActive={activeTab === 'profile'}
                  >
                    <User className="mr-2" />
                    <span>{t('profile')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('certifications')} 
                    onClick={() => handleTabChange('certifications')}
                    isActive={activeTab === 'certifications'}
                  >
                    <Bookmark className="mr-2" />
                    <span>{t('certifications')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('services')} 
                    onClick={() => handleTabChange('services')}
                    isActive={activeTab === 'services'}
                  >
                    <Briefcase className="mr-2" />
                    <span>{t('services')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('calendar.calendarView')}
                    onClick={() => handleTabChange('calendar')}
                    isActive={activeTab === 'calendar'}
                  >
                    <CalendarDays className="mr-2" />
                    <span>{t('calendar.calendar')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide
                    tooltip={t('upcoming')}
                    onClick={() => handleTabChange('upcoming')}
                    isActive={activeTab === 'upcoming'}
                    showBadge={true}
                    unreadCount={upcomingUnreadCount}
                    countRefreshTrigger={upcomingUnreadCount}
                  >
                    <Clock className="mr-2" />
                    <span>{t('upcoming')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide
                    tooltip={t('history')}
                    onClick={() => handleTabChange('history')}
                    isActive={activeTab === 'history'}
                    showBadge={true}
                    unreadCount={historyUnreadCount}
                    countRefreshTrigger={historyUnreadCount}
                  >
                    <History className="mr-2" />
                    <span>{t('history')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('lateCancellationRequests.title')} 
                    onClick={() => handleTabChange('late-cancellations')}
                    isActive={activeTab === 'late-cancellations'}
                  >
                    <AlertCircle className="mr-2" />
                    <span>{t('lateCancellationRequests.title')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('availability')} 
                    onClick={() => handleTabChange('availability')}
                    isActive={activeTab === 'availability'}
                  >
                    <Calendar className="mr-2" />
                    <span>{t('availability')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('finances')} 
                    onClick={() => handleTabChange('finances')}
                    isActive={activeTab === 'finances'}
                  >
                    <DollarSign className="mr-2" />
                    <span>{t('finances')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('reports')} 
                    onClick={() => handleTabChange('reports')}
                    isActive={activeTab === 'reports'}
                  >
                    <FileText className="mr-2" />
                    <span>{t('reports')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('settings')} 
                    onClick={() => handleTabChange('settings')}
                    isActive={activeTab === 'settings'}
                  >
                    <Settings className="mr-2" />
                    <span>{t('settings')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarContent>
            <PractitionerFooter onSignOut={handleSignOut} userEmail={user?.email} />
          </Sidebar>
          <SidebarInset className="bg-gray-50 flex-1 transition-all duration-300 ease-in-out">
            <header className="sticky top-0 z-40 bg-white border-b h-16 flex items-center px-4">
              <SidebarTrigger className="mr-4" />
              
            </header>
            <div className="w-full p-6">
              <div className="bg-white rounded-lg p-6 shadow-sm w-full">
                {profileMissing && (
                  <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">
                    <p className="font-medium">Practitioner profile not found.</p>
                    <p className="text-sm mt-1">Please complete your practitioner profile in Settings to unlock all features.</p>
                  </div>
                )}
                {renderTabContent}
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
      
      {/* Refund Notification System */}
      {user?.id && (
        <RefundNotificationSystem onNavigateToRefund={handleNavigateToRefund} />
      )}
      
    </div>
  );
};

// Simplified footer component with stable layout
function PractitionerFooter({ onSignOut, userEmail }: { onSignOut: () => void; userEmail?: string }) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();
  const { t } = useTranslation('dashboard');

  return (
    <SidebarFooter className="border-t mt-auto p-4">
      <SidebarMenuItem>
        <SidebarMenuButton 
          tooltip={t('logout')} 
          onClick={onSignOut}
          className="text-gray-600 hover:text-red-600 justify-center"
        >
          <LogOut className={expanded || isMobile ? "mr-2" : ""} />
          {(expanded || isMobile) && <span>{t('logout')}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
      {userEmail && (
        <div className={`mt-2 text-xs text-gray-500 px-2 transition-opacity duration-200 ${
          expanded || isMobile ? 'opacity-100' : 'opacity-0'
        }`}>
          {(expanded || isMobile) && (
            <span>{t('loggedInAs')} {userEmail}</span>
          )}
        </div>
      )}
    </SidebarFooter>
  );
}

export default PractitionerDashboard;
